import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  OnChanges,
  Output,
  ViewChild,
  ChangeDetectorRef,
  SimpleChanges,
  OnDestroy
} from '@angular/core';

import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { Location, SearchType } from '../../../../shared/models/search-bar';
import { DataService } from '../../../../shared/data/data.service';
import { debounceTime, Observable } from 'rxjs';

declare const bootstrap: any;

@Component({
  selector: 'app-location-picker',
  standalone: false,
  templateUrl: './location-picker.component.html',
  styleUrl: './location-picker.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationPickerComponent),
      multi: true
    }
  ]
})
export class LocationPickerComponent
  implements OnInit, OnChanges, ControlValueAccessor, OnDestroy {

  public isLoading: boolean = false;

  @Input() label: string = '';
  @Input() formControlName!: string;
  @Input() keyToPatch: string = 'code';
  @Input() searchType: SearchType = SearchType.FLIGHT;
  @Input() excludeAirport: string | null = null;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropDown') dropDown!: ElementRef<HTMLElement>;

  locations$: Observable<Location[]> | null = null;
  locations: Location[] = [];

  searchInputControl: FormControl = new FormControl('');
  selectedLocation: Location | null = null;

  @Output() locationSelected = new EventEmitter<Location>();

  value: any;

  private _bodyScrollLock?: {
    scrollY: number;
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    overflow: string;
  };

  private _dropdownHiddenHandler?: () => void;

  private lockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (this._bodyScrollLock) return;

    const body = document.body;
    this._bodyScrollLock = {
      scrollY: window.scrollY || window.pageYOffset || 0,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this._bodyScrollLock.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (!this._bodyScrollLock) return;

    const body = document.body;
    const { scrollY, position, top, left, right, width, overflow } = this._bodyScrollLock;

    body.style.position = position;
    body.style.top = top;
    body.style.left = left;
    body.style.right = right;
    body.style.width = width;
    body.style.overflow = overflow;

    this._bodyScrollLock = undefined;
    window.scrollTo(0, scrollY);
  }

  private ensureDropdownHiddenListener(): void {
    if (this._dropdownHiddenHandler) return;
    const el = this.dropDown?.nativeElement;
    if (!el) return;

    this._dropdownHiddenHandler = () => {
      this.unlockBodyScroll();
    };

    el.addEventListener('hidden.bs.dropdown', this._dropdownHiddenHandler);
  }

  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly dataService: DataService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['excludeAirport'] && this.excludeAirport && this.locations?.length) {

      this.locations = this.locations.filter(
        loc => String(loc.code).toUpperCase() !== String(this.excludeAirport).toUpperCase()
      );

      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {

    setTimeout(() => {
      if (!this.selectedLocation) {
        this.resolveLocationFromValue(this.value);
      }
    });

    this.searchInputControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(searchTerm => this.searchLocation(searchTerm));
  }

  public clearSearchInput(): void {
    this.searchInputControl.setValue('');
  }

  addFocus(): void {
    if (window?.innerWidth <= 767.98) {
      this.lockBodyScroll();
      this.ensureDropdownHiddenListener();
    }
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  private getStoredCoordinates(): { lat: number; lon: number } {

    const storedCoords = localStorage.getItem('userCoords');

    let lat = 0;
    let lon = 0;

    if (storedCoords) {

      try {

        const parsed = JSON.parse(storedCoords);

        lat = parsed?.coords?.lat ?? parsed?.lat ?? 0;
        lon = parsed?.coords?.lon ?? parsed?.lon ?? 0;

      } catch {}
    }

    return { lat, lon };
  }

  searchLocation(searchTerm: string): void {

    if (!searchTerm || searchTerm.length < 3) {

      this.locations = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    const { lat, lon } = this.getStoredCoordinates();

    const list$ =
      this.searchType === SearchType.FLIGHT
        ? this.dataService.getAirportList(searchTerm, lat, lon)
        : this.dataService.getCarLocationList(searchTerm, lat, lon);

    list$.subscribe((locations: Location[]) => {

      if (this.excludeAirport) {

        this.locations = locations.filter(
          loc => String(loc.code).toUpperCase() !== String(this.excludeAirport).toUpperCase()
        );

      } else {

        this.locations = locations;
      }

      this.isLoading = false;
    });
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(obj: any): void {

    this.value = obj;

    if (!obj) {

      this.selectedLocation = null;
      this.searchInputControl.setValue('', { emitEvent: false });
      return;
    }

    this.resolveLocationFromValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setLocation(location: Location, locator: HTMLElement): void {

    this.selectedLocation = location;

    this.value = location[this.keyToPatch as keyof Location];

    this.onChange(this.value);

    this.onTouched();

    this.locationSelected.emit(location);

    this.closeDropDown(locator);
  }

  public closeDropDown(locator: HTMLElement): void {

    const dropdown = bootstrap.Dropdown.getInstance(locator);

    dropdown?.hide();
    this.unlockBodyScroll();
  }

  ngOnDestroy(): void {
    if (this._dropdownHiddenHandler && this.dropDown?.nativeElement) {
      this.dropDown.nativeElement.removeEventListener('hidden.bs.dropdown', this._dropdownHiddenHandler);
    }
    this._dropdownHiddenHandler = undefined;
    this.unlockBodyScroll();
  }

  public useCurrentLocation(locator: HTMLElement): void {

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition((pos) => {

      const lat = pos.coords.latitude;
      const lon = pos.coords.longitude;

      if (this.searchType === SearchType.FLIGHT) {

        this.dataService.getNearestAirport(lat, lon)
          .subscribe(loc => {

            if (!loc) return;

            this.setLocation(loc, locator);
          });
      }

      else if (this.searchType === SearchType.CAR) {

        this.dataService.getNearestCarLocation(lat, lon)
          .subscribe(loc => {

            if (!loc) return;

            this.setLocation(loc, locator);
          });
      }

    });
  }

  private resolveLocationFromValue(val: any): void {

    if (!val) return;

    const q = String(val);

    const looksNumeric = /^\d+$/.test(q);

    const treatAsCar =
      this.searchType === SearchType.CAR || looksNumeric;

    if (treatAsCar) {

      this.dataService.getCarLocationById(q)
        .subscribe(loc => {

          this.selectedLocation = loc || null;

          this.cdr.detectChanges();
        });

    } else {

      this.dataService.getAirportList(q)
        .subscribe(locations => {

          let filteredLocations = locations;

          if (this.excludeAirport) {

            filteredLocations = locations.filter(
              loc => String(loc.code).toUpperCase()
                !== String(this.excludeAirport).toUpperCase()
            );
          }

          const key = this.keyToPatch as keyof Location;

          this.selectedLocation =
            filteredLocations.find(
              loc => String(loc[key] ?? '').toUpperCase()
                === q.toUpperCase()
            ) || null;

          this.cdr.detectChanges();
        });
    }
  }
}


// outside class
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {

  if ([lat1, lon1, lat2, lon2].some(v => !isFinite(v))) return NaN;

  const toRad = (v: number) => v * Math.PI / 180;

  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) ** 2;

  const c =
    2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}
